import * as THREE from "three";
import { defineModel, benchWork, type Part } from "../../lib";

// A sail-driven windmill. It shows the two headline features together:
//  • VARIANTS as construction stages — foundation → lower → upper → complete.
//  • a looping ANIMATION ("spin") that turns only the sails, about their hub.
// The sail parts are named "sails" so the spin track can rotate them as one
// node about the hub pivot while the tower and cap stay put.
const STONE = "#5a5a5a";
const SCAFFOLD = "#6b4a26";
const BODY = "#d9cdb0";
const CAP = "#5a4326";
const CLOTH = "#efe7d2";

const HUB_Y = 1.0;          // height of the sail hub on the finished tower
const HUB_Z = 0.3;          // how far the hub stands proud of the front face

function foundationSlab(w: number, t = 0.14): Part {
  return { geometry: new THREE.BoxGeometry(w, t, w), color: STONE, position: [0, t / 2, 0] };
}

function scaffold(w: number, h: number): Part[] {
  const o = w / 2;
  return ([[-o, -o], [o, -o], [-o, o], [o, o]] as const).map(([x, z]) => ({
    geometry: new THREE.CylinderGeometry(0.025, 0.025, h, 4), color: SCAFFOLD, position: [x, h / 2, z],
  }));
}

function tower(h: number): Part {
  return { geometry: new THREE.CylinderGeometry(0.26, 0.36, h, 10), color: BODY, position: [0, 0.14 + h / 2, 0] };
}

// The four cloth sails in an X, plus the hub — all named "sails" so they turn
// together. Authored centred on the hub so the spin pivot can sit there.
function sails(): Part[] {
  const out: Part[] = [
    { geometry: new THREE.CylinderGeometry(0.05, 0.05, 0.08, 8), color: CAP,
      position: [0, HUB_Y, HUB_Z], rotation: [Math.PI / 2, 0, 0], name: "sails" },
  ];
  for (let i = 0; i < 4; i++) {
    const ang = i * Math.PI / 2 + Math.PI / 4;
    out.push({
      geometry: new THREE.BoxGeometry(0.06, 0.5, 0.02), color: CLOTH,
      position: [Math.cos(ang) * 0.25, HUB_Y + Math.sin(ang) * 0.25, HUB_Z + 0.04],
      rotation: [0, 0, ang - Math.PI / 2], name: "sails",
    });
  }
  return out;
}

export const windmill = defineModel({
  id: "windmill",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Windmill",
  tags: ["building", "mill", "medieval", "animated"],
  summary: "A round mill tower whose sails turn; built across four stages.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => [foundationSlab(0.86)] },
    { id: "stage-1", label: "Lower", parts: () => [foundationSlab(0.86), tower(0.45), ...scaffold(0.7, 0.75)] },
    { id: "stage-2", label: "Upper", parts: () => [foundationSlab(0.86), tower(0.8), ...scaffold(0.7, 1.1)] },
    {
      id: "complete", label: "Complete", description: "The finished mill with cap and sails.",
      parts: () => [
        foundationSlab(0.86),
        tower(0.95),
        { geometry: new THREE.CylinderGeometry(0.02, 0.3, 0.22, 10), color: CAP, position: [0, 1.2, 0] },
        ...sails(),
      ],
    },
  ],
  animations: [
    {
      id: "spin",
      label: "Sails turning",
      duration: 6,
      loop: true,
      // Only meaningful once the sails exist.
      variants: ["complete"],
      tracks: [
        {
          target: "sails",
          pivot: [0, HUB_Y, HUB_Z],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 6, value: [0, 0, -Math.PI * 2] },
          ],
        },
      ],
    },
  ],
  // Worker choreography, ported 1:1 from age-of-mcp's windmill: a miller works
  // the bench at the front of the mill (bench-work program).
  stations: [
    { id: "front", position: [0, 0.14, 0.42], facing: Math.PI, description: "Where the miller works." },
  ],
  interactions: [
    { id: "work", label: "Working the mill", station: "front", variants: ["complete"], ...benchWork() },
  ],
});
